import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UtensilsCrossed, ArrowLeft, Building2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { restaurantAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const RestaurantSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, user: authedUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  // True when a logged-in user is adding the restaurant_partner role
  // to their existing account.
  const isAddRoleMode = !!authedUser;

  // Capture ?ref=<code> from the URL on mount and persist to the same
  // localStorage key SignupPage uses. Forwarded to the register call
  // below so referral attribution survives the role-specific landing.
  useEffect(() => {
    const refCode = new URLSearchParams(location.search).get('ref');
    if (refCode) {
      localStorage.setItem('bc_ref_code', refCode.trim().toUpperCase());
    }
  }, [location.search]);

  const [form, setForm] = useState({
    restaurantName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    city: '',
    address: '',
    openingHours: '09:00-22:00',
    vatNumber: '',
    fulfillmentMode: 'self_delivery',
    foodBusinessRegistrationNumber: '',
    registeredLocalAuthority: '',
    allergenPolicyAcknowledged: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAddRoleMode) {
      if (form.password !== form.confirmPassword) {
        toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
        return;
      }
      if (!form.restaurantName || !form.email || !form.password || !form.phone || !form.city) {
        toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
        return;
      }
    } else if (!form.restaurantName || !form.phone || !form.city) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    if (!form.foodBusinessRegistrationNumber.trim()) {
      toast({
        title: 'Food business registration required',
        description: 'Every UK food business must be registered with its local authority. Enter your registration reference.',
        variant: 'destructive'
      });
      return;
    }

    if (!form.allergenPolicyAcknowledged) {
      toast({
        title: 'Allergen policy acknowledgement required',
        description: 'You must acknowledge the allergen disclosure policy (Food Information Regulations 2014).',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await restaurantAPI.register({
        restaurantName: form.restaurantName,
        // Email + password are ignored server-side when authed.
        email: isAddRoleMode ? authedUser.email : form.email,
        password: isAddRoleMode ? 'addrole-placeholder-ignored' : form.password,
        phone: form.phone,
        city: form.city,
        address: form.address || null,
        openingHours: form.openingHours,
        vatNumber: form.vatNumber || null,
        fulfillmentMode: form.fulfillmentMode,
        foodBusinessRegistrationNumber: form.foodBusinessRegistrationNumber.trim(),
        registeredLocalAuthority: form.registeredLocalAuthority.trim() || null,
        allergenPolicyAcknowledged: form.allergenPolicyAcknowledged,
        referredBy: localStorage.getItem('bc_ref_code') || undefined
      });

      if (isAddRoleMode) {
        toast({ title: 'Restaurant role added', description: 'Welcome to your restaurant dashboard.' });
        try { await refreshUser?.(); } catch (_) {}
      } else {
        toast({ title: 'Success', description: 'Restaurant registered! Logging you in...' });
        await login(form.email, form.password);
      }
      navigate('/restaurant/dashboard');
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error.response?.data?.detail || 'Could not register restaurant',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute left-4 top-4"
            onClick={() => navigate('/')}
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="mx-auto mb-4 p-3 bg-[#1F3A68] rounded-full w-fit">
            <UtensilsCrossed size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl text-[#1F3A68]">
            {isAddRoleMode ? 'Add Restaurant Partner role' : 'Restaurant Partner Signup'}
          </CardTitle>
          <CardDescription>Join Ariv's food delivery network</CardDescription>
        </CardHeader>
        <CardContent>
          {isAddRoleMode && (
            <div className="mb-4 p-3 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
              <p className="font-semibold">Adding Restaurant Partner role to your existing account</p>
              <p className="text-xs text-[#1F3A68]/80 mt-0.5 break-all">Linked to {authedUser?.email}.</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Restaurant Name *</Label>
              <Input
                value={form.restaurantName}
                onChange={e => setForm(p => ({...p, restaurantName: e.target.value}))}
                placeholder="Your Restaurant Name"
                data-testid="restaurant-name-input"
              />
            </div>

            {isAddRoleMode ? (
              <div>
                <Label>Phone *</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                  placeholder="+44 123 456 7890"
                  data-testid="restaurant-phone-input"
                />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(p => ({...p, email: e.target.value}))}
                      placeholder="restaurant@example.com"
                      data-testid="restaurant-email-input"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                      placeholder="+44 123 456 7890"
                      data-testid="restaurant-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={e => setForm(p => ({...p, password: e.target.value}))}
                      placeholder="••••••••"
                      data-testid="restaurant-password-input"
                    />
                  </div>
                  <div>
                    <Label>Confirm Password *</Label>
                    <Input
                      type="password"
                      value={form.confirmPassword}
                      onChange={e => setForm(p => ({...p, confirmPassword: e.target.value}))}
                      placeholder="••••••••"
                      data-testid="restaurant-confirm-password-input"
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City *</Label>
                <Input 
                  value={form.city}
                  onChange={e => setForm(p => ({...p, city: e.target.value}))}
                  placeholder="London"
                  data-testid="restaurant-city-input"
                />
              </div>
              <div>
                <Label>Opening Hours</Label>
                <Input 
                  value={form.openingHours}
                  onChange={e => setForm(p => ({...p, openingHours: e.target.value}))}
                  placeholder="09:00-22:00"
                  data-testid="restaurant-hours-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Address (Optional)</Label>
              <Input 
                value={form.address}
                onChange={e => setForm(p => ({...p, address: e.target.value}))}
                placeholder="123 High Street, London"
                data-testid="restaurant-address-input"
              />
            </div>
            
            {/* Fulfillment Mode Selection */}
            <div>
              <Label className="mb-2 block">Delivery Model *</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setForm(p => ({...p, fulfillmentMode: 'self_delivery'}))}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    form.fulfillmentMode === 'self_delivery'
                      ? 'border-green-500 bg-green-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-xl mb-1">🏪</div>
                  <p className="font-semibold text-sm text-gray-800">Self-Delivery</p>
                  <p className="text-xs text-gray-500 mt-0.5">You have your own riders</p>
                  <p className="text-xs font-medium text-green-600 mt-1">12% commission · Live immediately</p>
                </button>
                <button
                  type="button"
                  onClick={() => setForm(p => ({...p, fulfillmentMode: 'platform_delivery'}))}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    form.fulfillmentMode === 'platform_delivery'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-xl mb-1">🛵</div>
                  <p className="font-semibold text-sm text-gray-800">Use Ariv riders</p>
                  <p className="text-xs text-gray-500 mt-0.5">Join the waiting list</p>
                  <p className="text-xs font-medium text-amber-600 mt-1">22% commission · Coming soon</p>
                </button>
              </div>
              {form.fulfillmentMode === 'self_delivery' ? (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-900">
                  Your restaurant goes live as soon as Ariv approves your account. You provide your own delivery
                  drivers and keep full control of the delivery experience. Lower commission too.
                </div>
              ) : (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                  Ariv is still building our own delivery network. If you pick this option your restaurant joins
                  our waiting list and goes live once we have verified riders covering your area. You can browse
                  the app and set up your menu in the meantime. Want to start earning right away? Use your own
                  riders for now and switch to Ariv riders later from your dashboard.
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Food Business Registration No. *</Label>
                <Input
                  value={form.foodBusinessRegistrationNumber}
                  onChange={e => setForm(p => ({...p, foodBusinessRegistrationNumber: e.target.value}))}
                  placeholder="e.g. FB-12345"
                  data-testid="restaurant-fsa-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your local-authority food business registration (required by UK law before trading).
                </p>
              </div>
              <div>
                <Label>Registering Local Authority</Label>
                <Input
                  value={form.registeredLocalAuthority}
                  onChange={e => setForm(p => ({...p, registeredLocalAuthority: e.target.value}))}
                  placeholder="e.g. London Borough of Hackney"
                  data-testid="restaurant-authority-input"
                />
              </div>
            </div>

            <div>
              <Label>VAT Number (Optional)</Label>
              <Input
                value={form.vatNumber}
                onChange={e => setForm(p => ({...p, vatNumber: e.target.value}))}
                placeholder="GB123456789"
                data-testid="restaurant-vat-input"
              />
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={form.allergenPolicyAcknowledged}
                onChange={e => setForm(p => ({...p, allergenPolicyAcknowledged: e.target.checked}))}
                className="w-4 h-4 rounded border-gray-300 mt-0.5"
                data-testid="restaurant-allergen-ack-input"
              />
              <span className="text-xs text-gray-600">
                I acknowledge that under the <strong>Food Information Regulations 2014</strong> I must declare
                the UK 14 major allergens for every item I sell, keep that information accurate, and answer
                customer allergen queries. Menu items cannot be published without an allergen declaration.
              </span>
            </div>

            <div className="flex items-start gap-2">
              <input type="checkbox" required className="w-4 h-4 rounded border-gray-300 mt-0.5" />
              <span className="text-xs text-gray-600">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Terms & Conditions</a>
                {' '}and{' '}
                <a href="/terms#data-protection" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Privacy Policy</a>,
                including the Restaurant Partner Terms (Section 6).
              </span>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
              disabled={loading}
              data-testid="restaurant-signup-btn"
            >
              {loading ? 'Registering...' : 'Register Restaurant'}
            </Button>
            
            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1F3A68] font-medium hover:underline">
                Login here
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RestaurantSignupPage;
